import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { CheckCircle2, CircleAlert, LoaderCircle, Landmark, MessageCircleMore, ReceiptText, ShieldCheck, TimerReset } from "lucide-react";
import {
  productApi,
  type ProductApi,
  type ProductInvoice,
  type ProductPendingAccount,
  type ProductRole,
  type ProductRuntimeInstance,
  type ProductSubscriptionSnapshot,
  type ProductTrialComponent,
  type ProductTrialLimit
} from "../product-api.js";
import { BusinessTrialStatus } from "./business-trial-status.js";

// WHY [BOL-MULTI-ACCOUNT-SUBSCRIPTION-001]: the owner's one commercial surface
// (rental plan §8.7/§11.5 ported): what is on trial, what is locked, the
// invoice and the bank transfer they reported; and, for the platform operator
// only, the queue of accounts to confirm, transfers to check by hand, trial
// values (blank = TBD) and the runtime instances. The browser renders exact
// server state and never decides trial, payment or routing on its own.

interface OperatorQueues {
  invoices: readonly ProductInvoice[];
  accounts: readonly ProductPendingAccount[];
  instances: readonly ProductRuntimeInstance[];
}

export function ProductSubscriptionPanel({
  role,
  businessSlug,
  canConfigure,
  businessTimezone,
  apiClient = productApi
}: {
  role: ProductRole;
  businessSlug: string;
  canConfigure: boolean;
  // WHY [BOL-SELF-SERVE-WEB-004]: the trial end is shown in the business's own
  // timezone. Optional so an unchanged caller still renders (browser zone).
  businessTimezone?: string | undefined;
  apiClient?: ProductApi;
}) {
  const subscriptionSection = useRef<HTMLElement>(null);
  useEffect(() => {
    // WHY [BOL-TRIAL-PAYMENT-NAV-875]: #settings alone leaves payment below
    // unrelated settings. Handle both cold section mount and a same-page CTA,
    // using native history and this existing read-only section. Do not run
    // any payment action or steal focus again on ordinary subscription loads.
    const openSubscription = () => {
      if (window.location.hash !== "#settings/subscription") return;
      subscriptionSection.current?.scrollIntoView({ block: "start" });
      subscriptionSection.current?.focus({ preventScroll: true });
    };
    openSubscription();
    window.addEventListener("hashchange", openSubscription);
    return () => window.removeEventListener("hashchange", openSubscription);
  }, []);
  const [snapshot, setSnapshot] = useState<ProductSubscriptionSnapshot | null>(null);
  const [queues, setQueues] = useState<OperatorQueues | null>(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [months, setMonths] = useState(1);
  const [proofDraft, setProofDraft] = useState({ transferReference: "", note: "" });
  const [amountDraft, setAmountDraft] = useState<Record<string, string>>({});
  const [rejectDraft, setRejectDraft] = useState<Record<string, string>>({});
  const [limitDraft, setLimitDraft] = useState<Record<string, string>>({});
  const [priceDraft, setPriceDraft] = useState("");
  const [bankDraft, setBankDraft] = useState({ bankName: "", accountName: "", accountNumber: "", instructions: "" });

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const result = await apiClient.subscription();
      setSnapshot(result.subscription);
      setPriceDraft(result.subscription.plan.monthlyPriceIdr === null ? "" : String(result.subscription.plan.monthlyPriceIdr));
      if (result.subscription.paymentInstructions) {
        const { bankName, accountName, accountNumber, instructions } = result.subscription.paymentInstructions;
        setBankDraft({ bankName, accountName, accountNumber, instructions });
      }
      if (result.subscription.platformOperator) {
        const [invoices, accounts, instances] = await Promise.all([apiClient.platformInvoices(), apiClient.pendingAccounts(), apiClient.platformInstances()]);
        setQueues({ invoices: invoices.invoices, accounts: accounts.accounts, instances: instances.instances });
      } else {
        setQueues(null);
      }
    } catch (cause) {
      setLoadError(errorMessage(cause, "Subscription details could not be loaded."));
    }
  }, [apiClient]);

  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, action: () => Promise<string>) => {
    setBusy(key);
    setMessage(null);
    try {
      const text = await action();
      setMessage({ tone: "success", text });
      await load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause, "That action could not be completed.") });
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return <section ref={subscriptionSection} id="subscription-payments" tabIndex={-1} className="product-settings-panel" aria-labelledby="subscription-title">
      <header><div><p className="eyebrow">Subscription &amp; Payments</p><h3 id="subscription-title">Plan and trial</h3></div><ReceiptText size={20} aria-hidden="true" /></header>
      <div className="product-settings-state product-settings-state--error" role="alert"><CircleAlert size={19} aria-hidden="true" /><div><strong>Subscription unavailable</strong><p>{loadError}</p></div></div>
    </section>;
  }
  if (!snapshot) {
    return <section ref={subscriptionSection} id="subscription-payments" tabIndex={-1} className="product-settings-panel" aria-labelledby="subscription-title">
      <header><div><p className="eyebrow">Subscription &amp; Payments</p><h3 id="subscription-title">Plan and trial</h3></div><ReceiptText size={20} aria-hidden="true" /></header>
      <p className="product-settings-state"><LoaderCircle className="spin" size={18} aria-hidden="true" />Loading subscription details.</p>
    </section>;
  }

  const owner = canConfigure && role === "OWNER";
  const { subscription, plan, trial, invoices, paymentInstructions, runtimeInstance } = snapshot;
  const openInvoice = invoices.find((invoice) => invoice.state === "REQUESTED" || invoice.state === "ISSUED" || invoice.state === "PROOF_SUBMITTED") ?? null;

  const submitProof = async (event: FormEvent) => {
    event.preventDefault();
    if (!openInvoice) return;
    await run("proof", async () => {
      const result = await apiClient.submitTransferProof(openInvoice.id, { ...proofDraft, expectedVersion: openInvoice.version });
      setProofDraft({ transferReference: "", note: "" });
      return `${result.invoice.invoiceNumber} is marked as paid by transfer. The platform checks the bank credit by hand and then turns the full system on.`;
    });
  };

  return <>
    <section ref={subscriptionSection} id="subscription-payments" tabIndex={-1} className="product-settings-panel" aria-labelledby="subscription-title">
      <header><div><p className="eyebrow">Subscription &amp; Payments</p><h3 id="subscription-title">{subscriptionHeadline(subscription.state, subscription.platformOwned)}</h3></div><ReceiptText size={20} aria-hidden="true" /></header>
      <p className="product-settings-note">{subscriptionCopy(subscription, plan)}</p>
      <BusinessTrialStatus variant="panel" subscription={subscription} businessTimezone={businessTimezone ?? browserTimeZone()} apiClient={apiClient} />

      {/* WHY [BOL-TRIAL-NOTICE-001]: timed trials start on first WhatsApp,
          never on component use/disclosure. Keep legacy terms only for legacy
          subscriptions; beta/platform accounts have no component trial gate. */}
      {showsLegacyComponentTrial(subscription) ? <>
        <div className="product-member-strip" aria-label="Trial components">{trial.map((component) => <span key={component.capabilityKey}><strong>{component.displayName}</strong><small>{trialComponentLabel(component)}</small></span>)}</div>
        {subscription.state === "TRIAL" && !subscription.trialDisclosedAt ? <div className="product-form-notice"><CircleAlert size={16} aria-hidden="true" /><span>Setup, QR pairing and tests use nothing. Each component's trial starts on its first real use after you acknowledge this. {owner ? <button className="secondary-button" type="button" disabled={busy !== null} onClick={() => void run("disclose", async () => { await apiClient.acknowledgeTrialDisclosure(); return "Trial terms acknowledged. Components now start on first real use."; })}>{busy === "disclose" ? "Saving…" : "I understand"}</button> : null}</span></div> : null}
      </> : null}

      {invoices.length === 0 ? <p className="product-settings-empty">No invoices yet.</p> : <div className="product-connection-list">{invoices.map((invoice) => <article key={invoice.id}>
        <span className="product-connection-icon"><Landmark size={18} aria-hidden="true" /></span>
        <div><strong>{invoice.invoiceNumber}</strong><small>{invoice.description}{invoice.amountIdr === null ? " · amount pending" : ` · ${formatIdr(invoice.amountIdr)}`}</small><p>{invoiceCopy(invoice)}</p></div>
        <span className={`product-connection-status product-connection-status--${invoiceTone(invoice.state)}`}>{invoiceStateLabel(invoice.state)}</span>
      </article>)}</div>}

      {owner && !subscription.platformOwned && subscription.state !== "ACTIVE" && !openInvoice ? <form className="product-settings-form" onSubmit={(event) => { event.preventDefault(); void run("activate", async () => { const result = await apiClient.requestActivation({ months }); return `${result.invoice.invoiceNumber} requested for ${months} month${months === 1 ? "" : "s"}.`; }); }}>
        <label><span>Months</span><select value={months} onChange={(event) => setMonths(Number(event.target.value))}>{[1, 2, 3, 6, 12].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
        <button className="primary-button" type="submit" disabled={busy !== null}>{busy === "activate" ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Requesting…</> : "Request invoice to turn on the full system"}</button>
      </form> : null}

      {owner && openInvoice?.state === "ISSUED" ? <form className="product-settings-form" onSubmit={(event) => void submitProof(event)}>
        {paymentInstructions ? <p className="product-settings-note">Transfer {openInvoice.amountIdr === null ? "the invoiced amount" : formatIdr(openInvoice.amountIdr)} to {paymentInstructions.bankName} · {paymentInstructions.accountName} · {paymentInstructions.accountNumber}. {paymentInstructions.instructions}</p> : <p className="product-settings-note">Bank transfer details will appear here once the platform sets them.</p>}
        <label><span>Transfer reference</span><input type="text" required maxLength={240} value={proofDraft.transferReference} onChange={(event) => setProofDraft((current) => ({ ...current, transferReference: event.target.value }))} placeholder="Bank, date and amount as shown on your transfer" /></label>
        <label><span>Note</span><input type="text" maxLength={1000} value={proofDraft.note} onChange={(event) => setProofDraft((current) => ({ ...current, note: event.target.value }))} /></label>
        <button className="primary-button" type="submit" disabled={busy !== null}>{busy === "proof" ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Sending…</> : "I made the transfer"}</button>
      </form> : null}
    </section>

    <section className="product-settings-panel" aria-labelledby="runtime-title">
      <header><div><p className="eyebrow">Your WhatsApp runtime</p><h3 id="runtime-title">{runtimeInstance ? instanceStateLabel(runtimeInstance.state) : "Not requested"}</h3></div><MessageCircleMore size={20} aria-hidden="true" /></header>
      <p className="product-settings-note">{runtimeInstance ? instanceCopy(runtimeInstance) : "Each business runs its own WhatsApp connection and agent, separate from every other business. Request yours; the platform prepares it and you pair your number by QR in the WhatsApp screen."}</p>
      {owner && !subscription.platformOwned && !runtimeInstance ? <button className="primary-button" type="button" disabled={busy !== null} onClick={() => void run("runtime", async () => { await apiClient.requestRuntimeInstance(); return "Your runtime was requested. The platform prepares it next."; })}>{busy === "runtime" ? "Requesting…" : "Request my own WhatsApp runtime"}</button> : null}
      {runtimeInstance?.state === "RUNNING" ? <button className="secondary-button" type="button" disabled={busy !== null} onClick={() => void run("select", async () => {
        // The routing cookie is set server-side after membership and RUNNING
        // state are proven; a full navigation then reaches the instance.
        await apiClient.selectInstance(businessSlug);
        window.setTimeout(() => window.location.assign("/"), 150);
        return "Switching to this business's runtime…";
      })}>Open this business's runtime</button> : null}
    </section>

    {snapshot.platformOperator && queues ? <section className="product-settings-panel" aria-labelledby="operator-title">
      <header><div><p className="eyebrow">Platform operator</p><h3 id="operator-title">Accounts, payments and trial values</h3></div><ShieldCheck size={20} aria-hidden="true" /></header>

      <h4>Accounts waiting for confirmation</h4>
      {queues.accounts.length === 0 ? <p className="product-settings-empty">No pending accounts.</p> : <div className="product-connection-list">{queues.accounts.map((account) => <article key={account.id}>
        <div><strong>{account.displayName}</strong><small>{account.email} · {new Date(account.createdAt).toLocaleString()}</small></div>
        <button className="secondary-button" type="button" disabled={busy !== null} onClick={() => void run(`confirm:${account.id}`, async () => { await apiClient.confirmAccount(account.id); return `${account.email} confirmed.`; })}>Confirm by hand</button>
      </article>)}</div>}

      <h4>Transfers to check</h4>
      {queues.invoices.length === 0 ? <p className="product-settings-empty">No open invoices.</p> : <div className="product-connection-list">{queues.invoices.map((invoice) => <article key={invoice.id}>
        <span className="product-connection-icon"><Landmark size={18} aria-hidden="true" /></span>
        <div><strong>{invoice.invoiceNumber}</strong><small>{invoice.description}{invoice.amountIdr === null ? " · amount not set" : ` · ${formatIdr(invoice.amountIdr)}`}</small><p>{invoice.transferReference ? `Owner reported: ${invoice.transferReference}${invoice.proofNote ? ` — ${invoice.proofNote}` : ""}` : invoiceCopy(invoice)}</p></div>
        <span className={`product-connection-status product-connection-status--${invoiceTone(invoice.state)}`}>{invoiceStateLabel(invoice.state)}</span>
        {invoice.state === "REQUESTED" || invoice.state === "ISSUED" ? <form className="product-settings-form" onSubmit={(event) => { event.preventDefault(); void run(`issue:${invoice.id}`, async () => { const amount = Number(amountDraft[invoice.id] ?? ""); await apiClient.issueInvoice(invoice.id, { amountIdr: amount, description: "", expectedVersion: invoice.version }); return `${invoice.invoiceNumber} issued for ${formatIdr(amount)}.`; }); }}>
          <label><span>Amount (IDR)</span><input type="number" min={1} step={1} required value={amountDraft[invoice.id] ?? (invoice.amountIdr ?? "")} onChange={(event) => setAmountDraft((current) => ({ ...current, [invoice.id]: event.target.value }))} /></label>
          <button className="secondary-button" type="submit" disabled={busy !== null}>Set amount and issue</button>
        </form> : null}
        {invoice.state === "PROOF_SUBMITTED" ? <div className="product-settings-form">
          <button className="primary-button" type="button" disabled={busy !== null} onClick={() => void run(`match:${invoice.id}`, async () => { const result = await apiClient.matchInvoice(invoice.id, { expectedVersion: invoice.version }); return `${invoice.invoiceNumber} matched. Full system on until ${result.subscription.currentPeriodEnd ? new Date(result.subscription.currentPeriodEnd).toLocaleDateString() : "the period end"}.`; })}>Bank credit checked — turn on</button>
          <label><span>Or reject with a reason</span><input type="text" maxLength={500} value={rejectDraft[invoice.id] ?? ""} onChange={(event) => setRejectDraft((current) => ({ ...current, [invoice.id]: event.target.value }))} /></label>
          <button className="product-revoke-button" type="button" disabled={busy !== null || !(rejectDraft[invoice.id] ?? "").trim()} onClick={() => void run(`reject:${invoice.id}`, async () => { await apiClient.rejectInvoice(invoice.id, { reason: (rejectDraft[invoice.id] ?? "").trim(), expectedVersion: invoice.version }); return `${invoice.invoiceNumber} rejected.`; })}>Reject</button>
        </div> : null}
      </article>)}</div>}

      <h4>Trial values (blank = not decided yet, never locks)</h4>
      <div className="product-connection-list">{trial.flatMap((component) => component.limits.map((limit) => <article key={`${limit.capabilityKey}:${limit.limitKind}`}>
        <span className="product-connection-icon"><TimerReset size={18} aria-hidden="true" /></span>
        <div><strong>{component.displayName}</strong><small>{limitKindLabel(limit)}</small></div>
        <form className="product-settings-form" onSubmit={(event) => { event.preventDefault(); void run(`limit:${limit.capabilityKey}:${limit.limitKind}`, async () => { const raw = (limitDraft[`${limit.capabilityKey}:${limit.limitKind}`] ?? draftFromLimit(limit)).trim(); const value = raw === "" ? null : (limit.limitKind === "TIME" ? Number(raw) * 86_400 : Number(raw)); await apiClient.setTrialLimit({ capabilityKey: limit.capabilityKey, limitKind: limit.limitKind, limitValue: value, expectedVersion: limit.version }); return `${component.displayName}: ${value === null ? "back to not decided" : trialLimitLabel({ ...limit, limitValue: value })}.`; }); }}>
          <label><span>{limit.limitKind === "TIME" ? "Days" : limitUnitLabel(limit)}</span><input type="number" min={1} step={1} value={limitDraft[`${limit.capabilityKey}:${limit.limitKind}`] ?? draftFromLimit(limit)} onChange={(event) => setLimitDraft((current) => ({ ...current, [`${limit.capabilityKey}:${limit.limitKind}`]: event.target.value }))} /></label>
          <button className="secondary-button" type="submit" disabled={busy !== null}>Save</button>
        </form>
      </article>))}</div>

      <h4>Plan price and bank transfer details</h4>
      <form className="product-settings-form" onSubmit={(event) => { event.preventDefault(); void run("price", async () => { const raw = priceDraft.trim(); await apiClient.setPlanPrice({ planKey: plan.planKey, monthlyPriceIdr: raw === "" ? null : Number(raw), expectedVersion: plan.version }); return raw === "" ? "Price set back to not decided." : `Monthly price set to ${formatIdr(Number(raw))}.`; }); }}>
        <label><span>{plan.displayName} monthly price (IDR, blank = not decided)</span><input type="number" min={1} step={1} value={priceDraft} onChange={(event) => setPriceDraft(event.target.value)} /></label>
        <button className="secondary-button" type="submit" disabled={busy !== null}>Save price</button>
      </form>
      <form className="product-settings-form" onSubmit={(event) => { event.preventDefault(); void run("bank", async () => { await apiClient.setPaymentInstructions({ ...bankDraft, expectedVersion: paymentInstructions?.version ?? null }); return "Bank transfer details saved."; }); }}>
        <label><span>Bank</span><input type="text" required maxLength={120} value={bankDraft.bankName} onChange={(event) => setBankDraft((current) => ({ ...current, bankName: event.target.value }))} /></label>
        <label><span>Account name</span><input type="text" required maxLength={160} value={bankDraft.accountName} onChange={(event) => setBankDraft((current) => ({ ...current, accountName: event.target.value }))} /></label>
        <label><span>Account number</span><input type="text" required pattern="[0-9 -]{4,40}" value={bankDraft.accountNumber} onChange={(event) => setBankDraft((current) => ({ ...current, accountNumber: event.target.value }))} /></label>
        <label><span>Instructions</span><input type="text" maxLength={2000} value={bankDraft.instructions} onChange={(event) => setBankDraft((current) => ({ ...current, instructions: event.target.value }))} /></label>
        <button className="secondary-button" type="submit" disabled={busy !== null}>Save bank details</button>
      </form>

      <h4>Runtime instances</h4>
      {queues.instances.length === 0 ? <p className="product-settings-empty">No instances requested.</p> : <div className="product-connection-list">{queues.instances.map((instance) => <article key={instance.id}>
        <div><strong>{instance.workspaceId}</strong><small>{instanceStateLabel(instance.state)}{instance.apiPort ? ` · port ${instance.apiPort}` : ""}</small><p>{instanceCopy(instance)}</p></div>
      </article>)}</div>}
    </section> : null}

    {message ? <p className={`product-action-message product-action-message--${message.tone}`} role={message.tone === "error" ? "alert" : "status"}>{message.tone === "success" ? <CheckCircle2 size={17} aria-hidden="true" /> : null}{message.text}</p> : null}
  </>;
}

export function subscriptionHeadline(state: ProductSubscriptionSnapshot["subscription"]["state"], platformOwned: boolean): string {
  if (platformOwned) return "Platform business";
  return state === "TRIAL" ? "Full System Trial" : state === "ACTIVE" ? "Full system on" : state === "PAST_DUE" ? "Payment overdue" : "Cancelled";
}

export function showsLegacyComponentTrial(subscription: ProductSubscriptionSnapshot["subscription"]): boolean {
  return !subscription.trialClockEligible && !subscription.directBeta && !subscription.platformOwned;
}

export function subscriptionCopy(subscription: ProductSubscriptionSnapshot["subscription"], plan: ProductSubscriptionSnapshot["plan"]): string {
  if (subscription.platformOwned) return "This is the platform's own business. It never pays itself.";
  if (subscription.state === "ACTIVE") {
    return `${plan.displayName} is active${subscription.currentPeriodEnd ? ` until ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}` : ""}.`;
  }
  // WHY [BOL-SELF-SERVE-WEB-004]: a new business is on the human-selected
  // three-day full-system trial that starts at its first confirmed WhatsApp
  // connection (R707/R769); the older per-component copy would misstate it.
  if (subscription.state === "TRIAL" && subscription.trialClockEligible === true) {
    return "Everything works for 3 days, including WhatsApp messages you approve. The 3 days start when you first connect WhatsApp, so setup does not use trial time. When the trial ends, your data and history stay.";
  }
  if (subscription.state === "TRIAL" && subscription.directBeta === true) {
    return "Beta access: everything works, with no trial clock.";
  }
  if (subscription.state === "TRIAL") {
    return `Every capability is usable during the trial. Each component has its own limit and starts only on its first real use${subscription.trialDisclosedAt ? "" : " after you acknowledge the trial terms"}. When a component reaches its limit only that component locks; your data stays.`;
  }
  return "Contact the platform to reactivate this business.";
}

export function trialComponentLabel(component: ProductTrialComponent): string {
  const limits = component.limits.map(trialLimitLabel).join(" · ");
  if (component.state === "NOT_STARTED") return `Not started · ${limits}`;
  if (component.state === "ACTIVE") return `Active${component.endsAt ? ` until ${new Date(component.endsAt).toLocaleDateString()}` : ""} · ${component.usedUnits} used · ${limits}`;
  return `Locked · ${limits}`;
}

export function trialLimitLabel(limit: ProductTrialLimit): string {
  if (limit.limitValue === null) return `${limitKindLabel(limit)}: not decided`;
  if (limit.limitKind === "TIME") return `${Math.round(limit.limitValue / 86_400)} day${Math.round(limit.limitValue / 86_400) === 1 ? "" : "s"}`;
  return `${limit.limitValue} ${limitUnitLabel(limit)}`;
}

export function limitKindLabel(limit: ProductTrialLimit): string {
  return limit.limitKind === "TIME" ? "Time window" : limit.limitKind === "USAGE" ? "Allowance" : "Credits";
}

function limitUnitLabel(limit: ProductTrialLimit): string {
  return limit.limitUnit.toLowerCase();
}

function draftFromLimit(limit: ProductTrialLimit): string {
  if (limit.limitValue === null) return "";
  return limit.limitKind === "TIME" ? String(Math.round(limit.limitValue / 86_400)) : String(limit.limitValue);
}

export function invoiceStateLabel(state: ProductInvoice["state"]): string {
  return state === "REQUESTED" ? "Amount pending" : state === "ISSUED" ? "Awaiting transfer" : state === "PROOF_SUBMITTED" ? "Checking transfer" : state === "MATCHED" ? "Paid" : state === "REJECTED" ? "Rejected" : "Void";
}

function invoiceTone(state: ProductInvoice["state"]): "connected" | "pending" | "revoked" | "unavailable" {
  return state === "MATCHED" ? "connected" : state === "REJECTED" || state === "VOID" ? "revoked" : "pending";
}

export function invoiceCopy(invoice: ProductInvoice): string {
  if (invoice.state === "REQUESTED") return "The platform sets the amount next.";
  if (invoice.state === "ISSUED") return "Transfer the amount, then report it below.";
  if (invoice.state === "PROOF_SUBMITTED") return "The platform checks the bank credit by hand.";
  if (invoice.state === "MATCHED") return `Paid. Covers ${invoice.periodStart ? new Date(invoice.periodStart).toLocaleDateString() : ""} to ${invoice.periodEnd ? new Date(invoice.periodEnd).toLocaleDateString() : ""}.`;
  if (invoice.state === "REJECTED") return invoice.rejectionReason ?? "Rejected.";
  return "Void.";
}

export function instanceStateLabel(state: ProductRuntimeInstance["state"]): string {
  return state === "PENDING_PROVISION" ? "Requested" : state === "PROVISIONED" ? "Prepared" : state === "RUNNING" ? "Running" : state === "PAUSED" ? "Paused" : "Retired";
}

function instanceCopy(instance: ProductRuntimeInstance): string {
  if (instance.state === "RUNNING") return "Your own WhatsApp connection and agent are running. Open it to pair your number and work in it.";
  // WHY [BOL-SELF-SERVE-WEB-002]: say why a request is waiting when the server reports it.
  if (instance.state === "PENDING_PROVISION" && instance.waitReason) return "Requested. We’re busy right now; your workspace starts as soon as there is room.";
  if (instance.state === "PENDING_PROVISION") return "Requested. The platform prepares your separate runtime next.";
  if (instance.state === "PROVISIONED") return "Prepared and starting.";
  return "Not available right now.";
}

function formatIdr(amount: number): string {
  return `IDR ${amount.toLocaleString("id-ID")}`;
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function errorMessage(value: unknown, fallback: string): string {
  return value instanceof Error && value.message ? value.message : fallback;
}
